<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <div class="modal-container requirement-editor-modal">
      <div class="modal-header">
        <h3>
          <i class="fas fa-edit"></i> Edit Requirement
        </h3>
        <button @click="close" class="modal-close">&times;</button>
      </div>

      <div v-if="loading" class="modal-body loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading requirement...</p>
      </div>

      <div v-else-if="error" class="modal-body error-state">
        <i class="fas fa-exclamation-circle"></i>
        <p>{{ error }}</p>
        <button @click="close" class="btn btn-secondary">Close</button>
      </div>

      <div v-else-if="requirement" class="modal-body">
        <!-- Basic Information Section -->
        <div class="section">
          <h4><i class="fas fa-info-circle"></i> Basic Information</h4>
          
          <div class="form-group">
            <label class="form-label required">Name</label>
            <input 
              type="text" 
              v-model="localRequirement.name"
              class="form-control"
              placeholder="Requirement name"
              required
            />
          </div>
          
          <div class="form-group">
            <label class="form-label required">Code</label>
            <input 
              type="text" 
              v-model="localRequirement.code"
              class="form-control"
              placeholder="unique_requirement_code"
              required
            />
            <small class="form-help">Unique identifier for this requirement</small>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label class="form-label required">Severity</label>
              <select v-model="localRequirement.severity" class="form-control">
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
            
            <div class="form-group">
              <label class="form-label">Order</label>
              <input 
                type="number" 
                v-model.number="localRequirement.order"
                class="form-control"
                min="0"
              />
              <small class="form-help">Execution order</small>
            </div>
          </div>
          
          <div class="form-group">
            <label class="form-label">Description</label>
            <textarea 
              v-model="localRequirement.description"
              class="form-control"
              rows="3"
              placeholder="Describe what this requirement checks..."
            ></textarea>
          </div>
        </div>

        <!-- Prompt Template Section -->
        <div class="section">
          <h4><i class="fas fa-code"></i> Prompt Template</h4>
          
          <div class="form-group">
            <label class="form-label">Prompt Template</label>
            <textarea 
              v-model="promptTemplate"
              class="form-control code-editor"
              rows="8"
              placeholder="Enter your prompt template here..."
            ></textarea>
            <small class="form-help">
              {{ promptTemplate?.length || 0 }} characters | {{ Math.ceil((promptTemplate?.length || 0) / 4) }} tokens (est.)
            </small>
          </div>
          
          <div class="form-group">
            <label class="form-label">Remediation Instructions</label>
            <textarea 
              v-model="localRequirement.remediation_instructions"
              class="form-control"
              rows="3"
              placeholder="Instructions for remediation if this requirement fails..."
            ></textarea>
          </div>
        </div>

        <!-- Classification Filters Section -->
        <div class="filters-section">
          <h4>
            <i class="fas fa-filter"></i> Classification Filters
          </h4>
          <p class="section-help">
            Configure which document and coverage types this requirement applies to.
            Leave empty to run on all types.
          </p>

          <!-- Document Types -->
          <div class="form-group">
            <label class="form-label">
              📄 Applicable Document Types
            </label>
            <div class="checkbox-grid">
              <label v-for="docType in documentTypes" :key="docType.value" class="checkbox-item">
                <input 
                  type="checkbox" 
                  :value="docType.value"
                  v-model="localRequirement.applicable_document_types"
                />
                <span>{{ docType.label }}</span>
              </label>
            </div>
            <small class="form-help">
              Select specific document types. Unchecked = applies to all document types.
            </small>
          </div>

          <!-- Coverage Types -->
          <div class="form-group">
            <label class="form-label">
              🛡️ Applicable Coverage Types
            </label>
            <div class="checkbox-grid">
              <label v-for="coverage in coverageTypes" :key="coverage.value" class="checkbox-item">
                <input 
                  type="checkbox" 
                  :value="coverage.value"
                  v-model="localRequirement.applicable_coverage_types"
                />
                <span>{{ coverage.label }}</span>
              </label>
            </div>
            <small class="form-help">
              For insurance requirements only. Unchecked = applies to all coverage types.
            </small>
          </div>

          <!-- Require Classification -->
          <div class="form-group">
            <label class="checkbox-label-lg">
              <input 
                type="checkbox" 
                v-model="localRequirement.require_classification"
              />
              <span class="checkbox-text">
                <strong>Require AI classification before running</strong>
                <small>Document must be successfully classified before this requirement executes</small>
              </span>
            </label>
          </div>
          
          <!-- Temporal Context Setting -->
          <div class="form-group">
            <label class="checkbox-label-lg">
              <input 
                type="checkbox" 
                v-model="localRequirement.requires_temporal_context"
              />
              <span class="checkbox-text">
                <strong>🕐 Include Current Date Context</strong>
                <small>Provides today's date to the AI for evaluating time-sensitive requirements (e.g., policy expiration checks, certificate validity)</small>
              </span>
            </label>
          </div>
        </div>

        <!-- Current Filters Summary -->
        <div v-if="hasFiltersConfigured" class="filters-summary">
          <h5><i class="fas fa-info-circle"></i> Current Configuration</h5>
          <div class="summary-content">
            <div v-if="localRequirement.applicable_document_types?.length" class="summary-item">
              <strong>Document Types:</strong>
              <div class="badge-list">
                <span v-for="type in localRequirement.applicable_document_types" :key="type" class="badge badge-doc-type">
                  {{ formatDocType(type) }}
                </span>
              </div>
            </div>
            <div v-else class="summary-item">
              <strong>Document Types:</strong> <em>All types</em>
            </div>

            <div v-if="localRequirement.applicable_coverage_types?.length" class="summary-item">
              <strong>Coverage Types:</strong>
              <div class="badge-list">
                <span v-for="cov in localRequirement.applicable_coverage_types" :key="cov" class="badge badge-coverage">
                  {{ formatCoverage(cov) }}
                </span>
              </div>
            </div>
            <div v-else class="summary-item">
              <strong>Coverage Types:</strong> <em>All coverages</em>
            </div>

            <div class="summary-item">
              <strong>Classification Required:</strong> {{ localRequirement.require_classification ? 'Yes' : 'No' }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="!loading && !error" class="modal-footer">
        <button
          v-if="projectId"
          type="button"
          class="btn btn-secondary"
          @click="showSiteMediaAttach = true"
        >
          <i class="fas fa-camera"></i>
          Attach Site Media
        </button>
        <button @click="close" class="btn btn-secondary">
          Cancel
        </button>
        <button @click="saveChanges" :disabled="saving" class="btn btn-primary">
          <i :class="saving ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i>
          {{ saving ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>

      <SiteMediaAttachModal
        v-if="projectId"
        v-model="showSiteMediaAttach"
        :project-id="projectId"
        :check-requirement-id="requirementId"
        @attached="onSiteMediaAttached"
      />
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import SiteMediaAttachModal from '@/components/site-media/SiteMediaAttachModal.vue';

export default {
  name: 'RequirementEditorModal',

  components: { SiteMediaAttachModal },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    requirementId: {
      type: [Number, String],
      default: null
    },
    projectId: {
      type: [Number, String],
      default: null
    }
  },

  emits: ['close', 'saved', 'site-media-attached'],
  
  data() {
    return {
      loading: false,
      saving: false,
      error: null,
      requirement: null,
      showSiteMediaAttach: false,
      localRequirement: {
        name: '',
        code: '',
        description: '',
        severity: 'MEDIUM',
        order: 0,
        remediation_instructions: '',
        applicable_document_types: [],
        applicable_coverage_types: [],
        require_classification: false,
        requires_temporal_context: true  // Default to true for time-sensitive checks
      },
      promptTemplate: '',
      
      documentTypes: [
        { value: 'CERTIFICATE', label: 'Certificate of Insurance' },
        { value: 'INSURANCE_POLICY', label: 'Insurance Policy' },
        { value: 'ENDORSEMENT', label: 'Policy Endorsement' },
        { value: 'LOSS_RUN', label: 'Loss Run Report' },
        { value: 'CONTRACT', label: 'Contract' },
        { value: 'AGREEMENT', label: 'Agreement' },
        { value: 'AMENDMENT', label: 'Amendment' },
        { value: 'QUOTE', label: 'Quote/Proposal' },
        { value: 'INVOICE', label: 'Invoice' },
        { value: 'W9_FORM', label: 'W-9 Form' },
        { value: 'TAX_FORM', label: 'Tax Form' },
        { value: 'OTHER', label: 'Other' }
      ],
      
      coverageTypes: [
        { value: 'GENERAL_LIABILITY', label: 'General Liability (GL)' },
        { value: 'WORKERS_COMP', label: 'Workers Compensation (WC)' },
        { value: 'AUTO_LIABILITY', label: 'Auto Liability (AL)' },
        { value: 'UMBRELLA', label: 'Umbrella/Excess' },
        { value: 'PROFESSIONAL_LIABILITY', label: 'Professional Liability (E&O)' },
        { value: 'PROPERTY', label: 'Property' },
        { value: 'CYBER', label: 'Cyber Liability' },
        { value: 'POLLUTION', label: 'Pollution Liability' },
        { value: 'BUILDERS_RISK', label: 'Builders Risk' },
        { value: 'INLAND_MARINE', label: 'Inland Marine' },
        { value: 'EMPLOYMENT_PRACTICES', label: 'Employment Practices (EPLI)' },
        { value: 'DIRECTORS_OFFICERS', label: 'Directors & Officers (D&O)' }
      ]
    };
  },
  
  computed: {
    hasFiltersConfigured() {
      return (
        this.localRequirement.applicable_document_types?.length > 0 ||
        this.localRequirement.applicable_coverage_types?.length > 0 ||
        this.localRequirement.require_classification
      );
    }
  },
  
  watch: {
    show(newVal) {
      if (newVal && this.requirementId) {
        this.loadRequirement();
      }
    },
    
    requirementId(newVal) {
      if (newVal && this.show) {
        this.loadRequirement();
      }
    }
  },
  
  methods: {
    onSiteMediaAttached(records) {
      this.$emit('site-media-attached', records);
      if (this.$toast?.success) {
        this.$toast.success(`Attached ${records.length} site capture(s)`);
      }
    },
    async loadRequirement() {
      if (!this.requirementId) return;
      
      this.loading = true;
      this.error = null;
      
      try {
        const response = await api.get(`/check-requirements/${this.requirementId}/`);
        this.requirement = response.data;
        
        // Initialize local editable copy with all fields
        this.localRequirement = {
          name: this.requirement.name || '',
          code: this.requirement.code || '',
          description: this.requirement.description || '',
          severity: this.requirement.severity || 'MEDIUM',
          order: this.requirement.order || 0,
          remediation_instructions: this.requirement.remediation_instructions || '',
          applicable_document_types: [...(this.requirement.applicable_document_types || [])],
          applicable_coverage_types: [...(this.requirement.applicable_coverage_types || [])],
          require_classification: this.requirement.require_classification || false,
          requires_temporal_context: this.requirement.requires_temporal_context !== false  // Default to true
        };
        
        // Extract prompt template from validation_logic
        const validationLogic = this.requirement.validation_logic || {};
        this.promptTemplate = validationLogic.prompt_template || '';
        
      } catch (err) {
        console.error('Error loading requirement:', err);
        this.error = 'Failed to load requirement details. The requirement may not exist or you may not have permission to view it.';
      } finally {
        this.loading = false;
      }
    },
    
    async saveChanges() {
      if (!this.requirement) return;
      
      // Validation
      if (!this.localRequirement.name || !this.localRequirement.code) {
        this.error = 'Name and Code are required fields';
        return;
      }
      
      this.saving = true;
      this.error = null;
      
      try {
        // Prepare payload with all editable fields
        const payload = {
          name: this.localRequirement.name,
          code: this.localRequirement.code,
          description: this.localRequirement.description,
          severity: this.localRequirement.severity,
          order: this.localRequirement.order,
          remediation_instructions: this.localRequirement.remediation_instructions,
          applicable_document_types: this.localRequirement.applicable_document_types,
          applicable_coverage_types: this.localRequirement.applicable_coverage_types,
          require_classification: this.localRequirement.require_classification,
          requires_temporal_context: this.localRequirement.requires_temporal_context,
          // Update validation_logic with prompt template
          validation_logic: {
            ...(this.requirement.validation_logic || {}),
            prompt_template: this.promptTemplate
          }
        };
        
        await api.patch(`/check-requirements/${this.requirement.id}/`, payload);
        
        this.$emit('saved', this.requirement);
        
        if (this.$toast) {
          this.$toast.success(`Requirement "${this.localRequirement.code}" updated successfully!`);
        }
        
        this.close();
        
      } catch (err) {
        console.error('Error saving requirement:', err);
        this.error = err.response?.data?.error || err.response?.data?.message || 'Failed to save changes. Please try again.';
        
        if (this.$toast) {
          this.$toast.error('Failed to save requirement changes');
        }
      } finally {
        this.saving = false;
      }
    },
    
    close() {
      this.$emit('close');
      // Reset state after animation
      setTimeout(() => {
        this.requirement = null;
        this.error = null;
        this.promptTemplate = '';
        this.localRequirement = {
          name: '',
          code: '',
          description: '',
          severity: 'MEDIUM',
          order: 0,
          remediation_instructions: '',
          applicable_document_types: [],
          applicable_coverage_types: [],
          require_classification: false,
          requires_temporal_context: true
        };
      }, 300);
    },
    
    formatDocType(type) {
      const found = this.documentTypes.find(t => t.value === type);
      return found ? found.label : type.replace(/_/g, ' ');
    },
    
    formatCoverage(type) {
      const found = this.coverageTypes.find(t => t.value === type);
      return found ? found.label : type.replace(/_/g, ' ');
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 1rem;
}

.modal-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

/* Sections */
.section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e5e7eb;
}

.section:last-child {
  border-bottom: none;
}

.section h4 {
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section h4 i {
  color: #3b82f6;
}

/* Form Elements */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control {
  width: 100%;
  padding: 0.625rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9375rem;
  transition: all 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control.code-editor {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
  font-size: 0.875rem;
  line-height: 1.5;
}

textarea.form-control {
  resize: vertical;
}

.form-label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  font-size: 0.9375rem;
}

.form-label.required::after {
  content: ' *';
  color: #ef4444;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3rem;
  color: #6b7280;
}

.loading-state i {
  font-size: 2rem;
  color: #3b82f6;
}

.error-state i {
  font-size: 2rem;
  color: #ef4444;
}

/* Requirement Info */
.requirement-info {
  background: #f9fafb;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.info-row {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  gap: 1rem;
}

.info-row .label {
  font-weight: 600;
  color: #6b7280;
  min-width: 80px;
}

.info-row .value {
  color: #1f2937;
}

.info-row .code {
  font-family: 'Courier New', monospace;
  background: #e5e7eb;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
}

.severity-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.severity-critical {
  background: #fee2e2;
  color: #991b1b;
}

.severity-high {
  background: #fed7aa;
  color: #9a3412;
}

.severity-medium {
  background: #fef3c7;
  color: #92400e;
}

.severity-low {
  background: #dbeafe;
  color: #1e40af;
}

/* Filters Section */
.filters-section {
  margin-bottom: 1.5rem;
}

.filters-section h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filters-section h4 i {
  color: #3b82f6;
}

.section-help {
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
  font-size: 0.9375rem;
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.75rem;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.checkbox-item:hover {
  background: #f9fafb;
  border-color: #3b82f6;
}

.checkbox-item input[type="checkbox"] {
  width: 1.125rem;
  height: 1.125rem;
  cursor: pointer;
}

.checkbox-item span {
  font-size: 0.875rem;
  color: #374151;
}

.checkbox-label-lg {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.checkbox-label-lg:hover {
  background: #f9fafb;
  border-color: #3b82f6;
}

.checkbox-label-lg input[type="checkbox"] {
  width: 1.125rem;
  height: 1.125rem;
  margin-top: 0.125rem;
  cursor: pointer;
}

.checkbox-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.checkbox-text strong {
  color: #1f2937;
  font-size: 0.9375rem;
}

.checkbox-text small {
  color: #6b7280;
  font-size: 0.8125rem;
}

.form-help {
  display: block;
  margin-top: 0.5rem;
  color: #6b7280;
  font-size: 0.8125rem;
}

/* Filters Summary */
.filters-summary {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  padding: 1rem;
}

.filters-summary h5 {
  margin: 0 0 0.75rem 0;
  font-size: 0.9375rem;
  color: #1e40af;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.summary-item {
  font-size: 0.875rem;
}

.summary-item strong {
  color: #374151;
  margin-right: 0.5rem;
}

.summary-item em {
  color: #6b7280;
}

.badge-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.375rem;
}

.badge {
  display: inline-block;
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.badge-doc-type {
  background: #e0e7ff;
  color: #3730a3;
}

.badge-coverage {
  background: #fce7f3;
  color: #831843;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 8px 8px;
}

.btn {
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.3);
}

.btn-secondary {
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #f3f4f6;
  color: #374151;
}
</style>

